import { useState, type FormEvent, type ReactNode } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'

import { leerToken, mensajeDeError } from '../../lib/api'
import { useSession } from '../../lib/session'
import { Button, ErrorMessage, Field, Loading, useToast } from '../../ui'
import { CordMark } from '../../ui/Cord'

/* ===========================================================================
   Crear cuenta.

   Mismo esqueleto que Entrar: una columna angosta, la marca arriba y nada más.
   Tres campos y el límite de la contraseña dicho antes de escribirla, no como
   reproche después de enviarla.
   =========================================================================== */

function Marco({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-dvh justify-center bg-bruma px-6 py-8 sm:py-12">
      <div className="w-full max-w-sm">
        <Link
          to="/plataforma"
          aria-label="Nuestra Boda — volver al inicio de la plataforma"
          className="inline-flex items-center gap-2.5 text-tinta"
        >
          <CordMark className="h-3.5 w-8 shrink-0 text-laton" />
          <span className="font-display text-base">Nuestra Boda</span>
        </Link>
        {children}
      </div>
    </main>
  )
}

export default function Registro() {
  const { user, cargando, registrar } = useSession()
  const navigate = useNavigate()
  const { ok } = useToast()

  // Con token guardado la sesión se está validando: esperar evita enseñar un
  // formulario de registro a quien ya tiene cuenta abierta.
  const [sesionPorRestaurar] = useState(() => leerToken() !== null)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function alEnviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (enviando) return

    setError(null)
    setEnviando(true)
    try {
      await registrar(name.trim(), email.trim(), password)
      ok('Cuenta creada.')
      navigate('/panel', { replace: true })
    } catch (fallo) {
      // Mensaje del servidor tal cual (correo repetido, contraseña corta…).
      // Nada de lo escrito se pierde: solo se corrige lo que él señala.
      setError(mensajeDeError(fallo))
      setEnviando(false)
    }
  }

  if (user) return <Navigate to="/panel" replace />
  if (cargando && sesionPorRestaurar) {
    return (
      <Marco>
        <Loading label="Comprobando tu sesión…" />
      </Marco>
    )
  }

  return (
    <Marco>
      <div className="mt-8 rounded-md border border-palo/60 bg-lienzo p-6">
        <div>
          <h1 className="font-display text-3xl text-tinta">Crea tu cuenta</h1>
          <p className="mt-2 text-sm text-palo-hondo">
            Con ella abres el panel de tu boda: invitados, confirmaciones, fotos y la guía de
            planificación.
          </p>
        </div>

        <form onSubmit={alEnviar} className="mt-8 flex flex-col gap-4">
          <Field
            label="Nombre"
            type="text"
            name="name"
            autoComplete="name"
            required
            value={name}
            onChange={(evento) => setName(evento.target.value)}
            disabled={enviando}
          />
          <Field
            label="Correo"
            type="email"
            name="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={email}
            onChange={(evento) => setEmail(evento.target.value)}
            disabled={enviando}
          />
          <Field
            label="Contraseña"
            type="password"
            name="password"
            autoComplete="new-password"
            hint="Mínimo 8 caracteres."
            required
            value={password}
            onChange={(evento) => setPassword(evento.target.value)}
            disabled={enviando}
          />

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <Button type="submit" full loading={enviando}>
            Crear mi cuenta
          </Button>
        </form>
      </div>

      <p className="mt-4 border-l border-palo pl-3 text-xs leading-relaxed text-palo-hondo">
        El plan gratis incluye una boda, hasta <span className="font-mono tabular">50</span>{' '}
        invitados y <span className="font-mono tabular">100</span> fotos. No pedimos tarjeta.
      </p>

      <hr className="regla mt-8" />
      <p className="mt-3 text-sm text-palo-hondo">
        ¿Ya tienes cuenta?{' '}
        <Link to="/entrar" className="text-tinta underline decoration-laton underline-offset-4">
          Entrar
        </Link>
      </p>
    </Marco>
  )
}
